import { useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { productsData } from '../data/products';
import { recipesData } from '../data/recipes';
import './ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const product = productsData.find(p => p.id === id);

  // Filter recipes that use this specific masala, ensuring always 3 cards
  const relatedRecipes = useMemo(() => {
    if (!product) return [];
    let matches = recipesData.filter(recipe => recipe.masala === product.name);
    
    // If fewer than 3 matches, fill with other random/popular recipes
    if (matches.length < 3) {
      const others = recipesData.filter(recipe => recipe.masala !== product.name);
      // Shuffle or just pick first few others to reach 3 total
      matches = [...matches, ...others].slice(0, 3);
    } else {
      // If more than 3, just take first 3 for consistent layout
      matches = matches.slice(0, 3);
    }
    
    return matches;
  }, [product]);

  // Fallback related products logic: Match category first, then ensure exactly 3
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    let matches = productsData.filter(p => p.category === product.category && p.id !== product.id);
    
    if (matches.length < 3) {
      const others = productsData.filter(p => p.category !== product.category && p.id !== product.id);
      matches = [...matches, ...others].slice(0, 3);
    } else {
      matches = matches.slice(0, 3);
    }
    return matches;
  }, [product]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  if (!product) {
    return (
      <div className="product-not-found page-container container">
        <h2 className="section-title">Product not found</h2>
        <p className="section-sub">The spice you are looking for might have moved to another garden.</p>
        <Link to="/products" className="btn-primary" style={{ marginTop: '2rem' }}>Back to Collection</Link>
      </div>
    );
  }

  return (
    <div className="product-details-page">
      {/* CINEMATIC HERO */}
      <section className="detail-hero-section">
        <div className="detail-hero-bg-accent" style={{ background: `linear-gradient(135deg, ${product.color}22, transparent)` }} />

        <div className="container">
          <div className="detail-hero-layout">
            <motion.div
              className="detail-hero-image-wrap"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <div className="premium-frame">
                <img src={product.img} alt={product.name} />
                <div className="frame-border" style={{ borderColor: product.color }} />
              </div>
            </motion.div>

            <motion.div
              className="detail-hero-content-wrap"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="cat-badge-wrap">
                <span className="cat-badge" style={{ backgroundColor: product.color }}>{product.category}</span>
              </div>
              <h1 className="detail-huge-title">{product.name}</h1>
              <p className="detail-tagline-serif">{product.tagline}</p>

              <div className="detail-meta-box">
                <div className="meta-item">
                  <span className="meta-label">Purity Grade</span>
                  <span className="meta-value">Stone-Ground</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Origin</span>
                  <span className="meta-value">Estate Sourced</span>
                </div>
              </div>

              <div className="detail-price-row">
                <span className="detail-price-value">{product.price}</span>
                <span className="unit">/ per pack</span>
              </div>

              <div className="offline-availability-notice">
                <div className="offline-icon-wrap">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </div>
                <div className="offline-text-content">
                  <strong>Available at Premium Retailers</strong>
                  <span>Find Ragini Masale at your nearest local store or contact us for trade inquiries.</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STORY & SENSORY SECTION */}
      <section className="detail-sensory-section">
        <div className="container">
          <div className="sensory-grid">
            <motion.div
              className="sensory-text-block"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="accent-sub">The Deep Dive</span>
              <h2 className="story-main-title">A Symphony of <span className="gradient-text">Aroma</span></h2>
              <p className="story-long-text">{product.story}</p>

              <div className="flavor-profile-card">
                <div className="flavor-icon">👃</div>
                <div className="flavor-content">
                  <h4>Sensory Profile</h4>
                  <p>{product.flavor}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="sensory-stats-block"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="ingredients-glass-list">
                <h3>The Blend</h3>
                <div className="ing-chips">
                  {product.ingredients.map((ing, i) => (
                    <span key={i} className="ing-chip">{ing}</span>
                  ))}
                </div>
              </div>

              <div className="usage-inspiration-box">
                <div className="usage-icon">🍲</div>
                <div className="usage-content">
                  <h4>Chef’s Note</h4>
                  <p>{product.usage}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* RECOMMENDED DISHES — CULINARY CREATIONS */}
      {relatedRecipes.length > 0 && (
        <section className="detail-recipes-section">
          <div className="container">
            <div className="section-header">
              <span className="accent-sub">Chef’s Corner</span>
              <h2 className="discovery-title">Dishes You Can <span className="gradient-text">Create</span></h2>
              <p>Explore authentic recipes curated for our artisanal blends</p>
            </div>

            <div className="recipe-cards-grid">
              {relatedRecipes.map((recipe, i) => (
                <motion.div
                  key={recipe.id}
                  className="dish-card-premium"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/recipe/${recipe.id}`} className="dish-card-link">
                    <div className="dish-image">
                      <img src={recipe.img} alt={recipe.name} />
                      <div className="dish-overlay">
                        <span className="dish-time">{recipe.time}</span>
                      </div>
                    </div>
                    <div className="dish-info">
                      <span className="dish-cat">{recipe.category}</span>
                      <h3>{recipe.name}</h3>
                      <p>{recipe.description.substring(0, 80)}...</p>
                      <span className="view-recipe">View Full Recipe →</span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED PRODUCTS */}
      <section className="related-discovery-section">
        <div className="container">
          <div className="discovery-header">
            <h2 className="discovery-title">Discover <span className="gradient-text">More Flavors</span></h2>
            <p>You may also like these curated spices</p>
          </div>

          <div className="related-slider-grid">
            {relatedProducts.map((rp, i) => (
              <motion.div
                key={rp.id}
                className="related-slide-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={`/product/${rp.id}`} className="related-link-card">
                  <div className="related-img-box">
                    <img src={rp.img} alt={rp.name} />
                  </div>
                  <div className="related-text-box">
                    <span className="rp-cat">{rp.category}</span>
                    <h4>{rp.name}</h4>
                    <span className="rp-price">{rp.price}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default ProductDetails;
